import axios from 'axios';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import routes from '../routes';
import GlobalHelper from '../utils/globalHelper';
import paths from './path';

let isRefreshing = false;
let failedRequestsQueue = [];
let refreshTokenPromise = null;

const handleRequestError = (err, hidePopError, refreshToken) => {
  const errorMessage =
    get(err, 'response.data.message') ||
    get(err, 'response.data.errorMessage') ||
    'An error occurred';

  if (!hidePopError && refreshToken) {
    toast.error(errorMessage);
  }

  const error = err instanceof Error ? err : new Error(errorMessage);
  error.message = errorMessage;
  error.errorCode = get(err, 'response.data.errorCode');
  error.errorData = get(err, 'response.data.data');
  error.response = err.response;
  throw error;
};

const retryOriginalRequest = async (config) => {
  const newConfig = {
    ...config,
    headers: {
      ...config.headers,
      Authorization: `${localStorage.getItem('token')}`,
    },
  };
  const response = await axios(newConfig);

  return response.data;
};

const NetworkApi = {
  requestTimeout: 30000,
  getDefaultHeaders: () => ({
    Accept: 'application/json',
    'Content-Type': 'application/json;charset=UTF-8',
  }),

  getHeaders: () => ({
    ...NetworkApi.getDefaultHeaders(),
  }),

  get: (route, headers) => NetworkApi.request('get', route, null, headers),
  post: (route, params, headers, hidePopError, responseType) =>
    NetworkApi.request('post', route, params, headers, hidePopError, responseType),
  put: (route, params, headers) => NetworkApi.request('put', route, params, headers),
  patch: (route, params, headers) => NetworkApi.request('patch', route, params, headers),
  delete: (route, headers) => NetworkApi.request('delete', route, null, headers),

  request: async (method, route, params, headers, hidePopError, responseType = null) => {
    const config = {
      method,
      url: route,
      data: params,
      headers: headers || NetworkApi.getDefaultHeaders(),
      timeout: NetworkApi.requestTimeout,
    };
    if (responseType) {
      config.responseType = responseType;
    }
    const refreshToken = localStorage.getItem('refreshToken');

    try {
      const response = await axios(config);

      return response.data;
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          await NetworkApi.handleTokenRefresh();

          return await retryOriginalRequest(config);
        } catch (refreshErr) {
          return handleRequestError(refreshErr, hidePopError, refreshToken);
        }
      } else {
        return handleRequestError(err, hidePopError, refreshToken);
      }
    }
  },
  handleTokenRefresh: async () => {
    if (isRefreshing) {
      return refreshTokenPromise;
    }

    isRefreshing = true;
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      toast.error('Refresh token is unavailable. Please log in again.');
      GlobalHelper.clearStorageWithoutLanguage();
      window.location.pathname = routes.LoginPage;
      throw new Error('No refresh token available');
    }

    refreshTokenPromise = (async () => {
      try {
        const response = await axios.post(paths.refreshTokenUrl, { refreshToken });
        const { data } = response;
        localStorage.setItem('refreshToken', data.data.refreshToken);
        localStorage.setItem('token', data.data.token);
        axios.defaults.headers.common.Authorization = `${data.data.token}`;

        const retryRequests = failedRequestsQueue.map(
          ({ config: requestConfig, resolve, reject }) => {
            const newRequestConfig = {
              ...requestConfig,
              headers: {
                ...requestConfig.headers,
                Authorization: `${data.data.token}`,
              },
            };
            return axios(newRequestConfig)
              .then((res) => resolve(res))
              .catch((error) => reject(error));
          }
        );

        await Promise.all(retryRequests);
        failedRequestsQueue = [];
      } catch (refreshErr) {
        console.log('Refresh Error Object:', refreshErr);

        failedRequestsQueue.forEach(({ reject }) => {
          reject(refreshErr);
        });
        failedRequestsQueue = [];

        console.error(
          'Refresh token failed. Clearing local storage and redirecting to login.',
          refreshErr
        );
        GlobalHelper.clearStorageWithoutLanguage();
        window.location.pathname = routes.LoginPage;
        throw refreshErr;
      } finally {
        isRefreshing = false;
        refreshTokenPromise = null;
      }
    })();

    return refreshTokenPromise;
  },
};

axios.interceptors.response.use(
  (response) => response,
  async (err) => {
    const { config } = err;

    if (err.response?.status === 401) {
      if (config.url.includes(paths.refreshTokenUrl)) {
        GlobalHelper.clearStorageWithoutLanguage();
        window.location.pathname = routes.LoginPage;
        return Promise.reject(err instanceof Error ? err : new Error(err));
      }

      if (!isRefreshing) {
        try {
          await NetworkApi.handleTokenRefresh();
          const newConfig = {
            ...config,
            headers: {
              ...config.headers,
              Authorization: `${localStorage.getItem('token')}`,
            },
          };
          return axios(newConfig);
        } catch (refreshErr) {
          GlobalHelper.clearStorageWithoutLanguage();
          window.location.pathname = routes.LoginPage;
          return Promise.reject(refreshErr instanceof Error ? refreshErr : new Error(refreshErr));
        }
      } else {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ config, resolve, reject });
        });
      }
    }

    return Promise.reject(err instanceof Error ? err : new Error(err));
  }
);

export default NetworkApi;
